import { setHeaderGlassGradient, roundedHeaderGlassPath } from './header-glass-shape.js';

export const initHeaderLanguage = () => {
    const root = document.querySelector('[data-header-dropdown-root]');
    if (!root) return;

    document.querySelectorAll('[data-header-language]').forEach((wrapper, index) => {
        const trigger = wrapper.querySelector('[data-header-language-trigger]');
        const menu = wrapper.querySelector('[data-header-language-menu]');
        if (!trigger || !menu || wrapper.dataset.initialized) return;
        wrapper.dataset.initialized = 'true';
        menu.id = menu.id || 'header-language-' + index;
        trigger.setAttribute('aria-controls', menu.id);
        let isOpen = false;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let frame = 0;
        let expansion = 0;
        let widthExpansion = 1;
        let textOpacity = 0;
        let closing = false;
        let surface, outlineLayer, outlinePath, placeholder, triggerStyle, geometry;

        const buildPath = (shape, expansion, inset, widthExpansion = 1) => {
            const { w, h, menuHeight, buttonStart, buttonEnd } = shape;
            const panelStart = buttonStart * (1 - widthExpansion);
            const panelEnd = buttonEnd + (w - buttonEnd) * widthExpansion;
            const bottom = h + menuHeight * Math.max(0, expansion);
            const points = expansion <= 0
                ? [[buttonStart + inset, inset], [buttonEnd - inset, inset], [buttonEnd - inset, h - inset], [buttonStart + inset, h - inset]]
                : [[buttonStart + inset, inset], [buttonEnd - inset, inset], [buttonEnd - inset, h - inset], [panelEnd - inset, h - inset], [panelEnd - inset, bottom - inset], [panelStart + inset, bottom - inset], [panelStart + inset, h - inset], [buttonStart + inset, h - inset]];
            return roundedHeaderGlassPath(points, Math.min(20, h / 2));
        };
        const setOutline = (layer, shape, expansion) => {
            const height = shape.h + shape.menuHeight * 1.04;
            const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + shape.w + '" height="' + height + '"><path d="' + buildPath(shape, expansion, 0.5) + '" fill="none" stroke="white" stroke-width="1"/></svg>';
            layer.style.setProperty('--dropdown-outline', 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '")');
        };
        const draw = () => {
            // Clip the backdrop itself to the same animated contour as the border.
            surface.style.setProperty('--dropdown-shape', 'path("' + buildPath(geometry, expansion, 0, widthExpansion) + '")');
            menu.style.clipPath = 'inset(0 0 ' + (geometry.menuHeight * (1 - expansion)) + 'px)';
            menu.style.setProperty('--language-opacity', textOpacity);
            outlinePath.setAttribute('d', buildPath(geometry, expansion, 0.5, widthExpansion));
        };
        // Match the closed button to the expanding outline, including its gradient.
        const syncBorder = () => {
            if (isOpen) return;
            const rect = trigger.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            setHeaderGlassGradient(trigger, trigger, rect.width, rect.height, 0);
            setOutline(trigger, { w: rect.width, h: rect.height, menuHeight: 0, buttonStart: 0, buttonEnd: rect.width }, 0);
            trigger.classList.add('header-language-trigger--border-ready');
        };
        syncBorder();
        const borderObserver = new ResizeObserver(syncBorder);
        borderObserver.observe(trigger);

        const restore = () => {
            window.cancelAnimationFrame(frame);
            const focused = document.activeElement === trigger;
            isOpen = false;
            closing = false;
            trigger.setAttribute('aria-expanded', 'false');
            menu.classList.remove('header-language__menu--open', 'header-language__menu--closing');
            menu.inert = false;
            wrapper.appendChild(menu);
            menu.removeAttribute('style');
            placeholder.replaceWith(trigger);
            trigger.classList.remove('header-language-trigger--glass-open');
            if (triggerStyle === null) trigger.removeAttribute('style');
            else trigger.setAttribute('style', triggerStyle);
            surface.remove();
            outlineLayer.remove();
            if (focused) trigger.focus({ preventScroll: true });
        };
        const animate = (target, complete) => {
            window.cancelAnimationFrame(frame);
            const from = expansion;
            const fromWidth = widthExpansion;
            const fromOpacity = textOpacity;
            const started = performance.now();
            const tick = (now) => {
                if (!isOpen) return;
                const t = reducedMotion.matches ? 1 : Math.min(1, (now - started) / (target ? 400 : 240));
                // Let the 120ms text fade finish before collapsing the surface.
                const shapeProgress = target ? t : Math.max(0, (t - 0.5) * 2);
                const eased = 1 - Math.pow(1 - shapeProgress, 3);
                expansion = t === 1 ? target : from + (target - from) * eased;
                widthExpansion = t === 1 ? target : fromWidth + (target - fromWidth) * eased;
                if (target) textOpacity = fromOpacity + (1 - fromOpacity) * eased;
                draw();
                if (t < 1) frame = window.requestAnimationFrame(tick);
                else if (complete) complete();
            };
            if (reducedMotion.matches) tick(started);
            else frame = window.requestAnimationFrame(tick);
        };
        const close = (immediate = false) => {
            if (!isOpen) return;
            if (immediate === true || reducedMotion.matches) {
                restore();
                return;
            }
            if (closing) return;
            closing = true;
            trigger.setAttribute('aria-expanded', 'false');
            const textStyle = window.getComputedStyle(menu);
            menu.style.setProperty('--language-close-opacity', textStyle.opacity);
            menu.classList.add('header-language__menu--closing');
            menu.inert = true;
            animate(0, restore);
        };
        const open = () => {
            if (!trigger.getClientRects().length) return;
            if (isOpen) {
                if (closing) {
                    textOpacity = Number.parseFloat(window.getComputedStyle(menu).opacity);
                    menu.style.setProperty('--language-opacity', textOpacity);
                    closing = false;
                    menu.inert = false;
                    menu.classList.remove('header-language__menu--closing');
                    trigger.setAttribute('aria-expanded', 'true');
                    animate(1);
                }
                return;
            }
            const rect = trigger.getBoundingClientRect();
            const rtl = window.getComputedStyle(trigger).direction === 'rtl';
            const width = document.documentElement.clientWidth;
            const edge = 12;
            triggerStyle = trigger.getAttribute('style');
            placeholder = document.createElement('span');
            placeholder.className = 'header-language__placeholder';
            placeholder.style.width = rect.width + 'px';
            placeholder.style.height = rect.height + 'px';
            placeholder.setAttribute('aria-hidden', 'true');
            trigger.replaceWith(placeholder);
            trigger.classList.add('header-language-trigger--glass-open');
            trigger.style.direction = rtl ? 'rtl' : 'ltr';
            trigger.style.top = rect.top + 'px';
            trigger.style.insetInlineStart = (rtl ? width - rect.right : rect.left) + 'px';
            surface = document.createElement('div');
            surface.className = 'header-dropdown-surface header-language-surface';
            surface.setAttribute('aria-hidden', 'true');
            outlineLayer = document.createElement('div');
            outlineLayer.className = 'header-dropdown-outline header-language-outline';
            outlineLayer.setAttribute('aria-hidden', 'true');
            root.append(surface, outlineLayer, trigger, menu);
            isOpen = true;
            closing = false;
            expansion = 0;
            widthExpansion = 1;
            textOpacity = 0;
            trigger.setAttribute('aria-expanded', 'true');
            menu.classList.add('header-language__menu--open');
            menu.style.direction = rtl ? 'rtl' : 'ltr';
            menu.style.maxWidth = Math.min(300, width - edge * 2) + 'px';
            menu.style.maxHeight = Math.max(0, window.innerHeight - rect.bottom - edge) + 'px';
            const menuWidth = menu.offsetWidth;
            const x = Math.max(edge, Math.min(rtl ? rect.left : rect.right - menuWidth, width - menuWidth - edge));
            menu.style.insetInlineStart = (rtl ? width - x - menuWidth : x) + 'px';
            menu.style.top = rect.bottom + 'px';
            const start = Math.min(x, rect.left);
            const end = Math.max(x + menuWidth, rect.right);
            geometry = { w: end - start, h: rect.height, menuHeight: menu.offsetHeight, buttonStart: rect.left - start, buttonEnd: rect.right - start };
            for (const layer of [surface, outlineLayer]) {
                layer.style.direction = rtl ? 'rtl' : 'ltr';
                layer.style.insetInlineStart = (rtl ? width - end : start) + 'px';
                layer.style.top = rect.top + 'px';
                layer.style.width = geometry.w + 'px';
                layer.style.height = (rect.height + menu.offsetHeight * 1.04) + 'px';
            }
            setHeaderGlassGradient(outlineLayer, trigger, rect.width, rect.height, menu.offsetHeight, geometry.w, geometry.buttonStart);
            // Keep one SVG in the DOM rather than decoding a new mask image each frame.
            const gradientImage = outlineLayer.style.getPropertyValue('--dropdown-border-gradient');
            const gradientMarkup = decodeURIComponent(gradientImage.slice(gradientImage.indexOf(',') + 1, -2));
            const gradientDocument = new DOMParser().parseFromString(gradientMarkup, 'image/svg+xml');
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('width', geometry.w);
            svg.setAttribute('height', rect.height + menu.offsetHeight * 1.04);
            const defs = document.importNode(gradientDocument.querySelector('defs'), true);
            const gradientId = 'header-language-border-' + index;
            defs.querySelector('linearGradient').id = gradientId;
            outlinePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            outlinePath.setAttribute('fill', 'none');
            outlinePath.setAttribute('stroke', 'url(#' + gradientId + ')');
            outlinePath.setAttribute('stroke-width', '1');
            svg.append(defs, outlinePath);
            outlineLayer.appendChild(svg);
            draw();
            animate(1);
        };

        trigger.addEventListener('click', () => {
            if (isOpen && !closing) close();
            else open();
        });
        trigger.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowDown' && !(event.key === 'Tab' && !event.shiftKey && isOpen)) return;
            const first = menu.querySelector('a[href]');
            if (!first) return;
            event.preventDefault();
            open();
            first.focus();
        });
        menu.addEventListener('keydown', (event) => {
            if (event.key !== 'Tab') return;
            const links = Array.from(menu.querySelectorAll('a[href]'));
            if (event.shiftKey && document.activeElement === links[0]) {
                event.preventDefault();
                close(true);
                trigger.focus();
            } else if (!event.shiftKey && document.activeElement === links[links.length - 1]) {
                close(true);
                trigger.focus();
            }
        });
        menu.addEventListener('click', (event) => {
            if (event.target.closest('a[href]')) close();
        });
        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || !isOpen) return;
            close();
            trigger.focus();
        });
        document.addEventListener('mousedown', (event) => {
            if (isOpen && !menu.contains(event.target) && !trigger.contains(event.target)) close();
        });
        document.addEventListener('focusin', (event) => {
            if (isOpen && !closing && !menu.contains(event.target) && event.target !== trigger) close();
        });
        window.addEventListener('scroll', (event) => {
            if (isOpen && !menu.contains(event.target)) close(true);
        }, true);
        window.addEventListener('resize', () => close(true));
        reducedMotion.addEventListener('change', () => close(true));
    });
};
